import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  LucideIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export interface NavItemConfig {
  to: string;
  icon: LucideIcon;
  labelKey?: string;
  label?: string;
  end?: boolean;
}

/**
 * An agency and the screens under it.
 *
 * A group has no `to` of its own on purpose. Making the header navigate as well
 * as expand means one click does two things, and the user who wanted to see the
 * screens lands on a page instead.
 */
export interface NavGroupConfig {
  key: string;
  icon: LucideIcon;
  labelKey?: string;
  label?: string;
  children: NavItemConfig[];
}

export type NavEntry = NavItemConfig | NavGroupConfig;

/**
 * How strongly a group claims the current path, 0 meaning not at all.
 *
 * Longest match wins, so `/anatel/paf/consistency` opens ANATEL on the strength
 * of `/anatel/paf/consistency` rather than tying with `/anatel/paf`. The
 * first-segment fallback exists for the detail routes, which are reached by
 * clicking a row and deliberately absent from the nav: `/anatel/p/53569002309`
 * matches no child `to` at all, and a reader who followed a row into a case is
 * exactly the one most likely to have lost track of where they are.
 */
function groupMatchScore(group: NavGroupConfig, pathname: string): number {
  const segment = (path: string) => path.split('/')[1] ?? '';
  let best = 0;
  for (const child of group.children) {
    if (pathname === child.to || pathname.startsWith(`${child.to}/`)) {
      best = Math.max(best, child.to.length);
    } else if (segment(child.to) && segment(child.to) === segment(pathname)) {
      best = Math.max(best, 1);
    }
  }
  return best;
}

function isGroup(entry: NavEntry): entry is NavGroupConfig {
  return 'children' in entry;
}

//: Width bounds, in px. The floor keeps the longest label ("Inteligência
//: sancionatória") from wrapping to three lines; the ceiling stops the sidebar
//: from eating a table. The default is the `w-56` the sidebar had before it
//: became resizable, so nobody's layout moves until they move it.
const MIN_WIDTH = 190;
const MAX_WIDTH = 420;
const DEFAULT_WIDTH = 224;
const RAIL_WIDTH = 60;
const WIDTH_KEY = 'agencias.sidebar.width';
const COLLAPSED_KEY = 'agencias.sidebar.collapsed';

function readStored<T>(key: string, fallback: T, parse: (raw: string) => T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : parse(raw);
  } catch {
    // Private mode, a disabled store, or no window at all under jsdom. A
    // sidebar that throws on load is worse than one at its default width.
    return fallback;
  }
}

function store(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* see readStored */
  }
}

export interface BreadcrumbItem {
  labelKey?: string;
  label?: string;
  to?: string;
}

export interface UserContextData {
  name: string;
  role: string;
  initials: string;
}

export interface SidebarLayoutProps {
  navigation?: NavEntry[];
  brandNameKey?: string;
  brandSubtitleKey?: string;
  user?: UserContextData;
  onLogout?: () => void;
  /** @deprecated Use `breadcrumbs` for a full trail. This prop renders a single label. */
  breadcrumbKey?: string;
  breadcrumbs?: BreadcrumbItem[];
  /**
   * Rendered right-aligned in the header. This app puts the theme, language and
   * timezone switchers here, which `ui-standards.md` requires to live in the
   * top-right. Local addition to the copied component.
   */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}

function NavItem({
  to,
  icon: Icon,
  labelKey,
  label,
  end = false,
  onClick,
  collapsed = false,
  nested = false,
}: NavItemConfig & { onClick?: () => void; collapsed?: boolean; nested?: boolean }) {
  const { t } = useTranslation('common');
  const displayLabel = labelKey ? t(labelKey, label ?? '') : label;

  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      // The label is the accessible name when it is visible; collapsed, the
      // icon is all that remains, so the name has to come from somewhere.
      title={collapsed ? displayLabel : undefined}
      aria-label={collapsed ? displayLabel : undefined}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 ${
          collapsed ? 'justify-center px-2' : nested ? 'pl-8 pr-3' : 'px-3'
        } py-2 rounded-lg text-sm font-medium transition-all group overflow-hidden ${
          isActive
            // NOT `text-primary-foreground`. That token is the contrast pair for
            // a SOLID `bg-primary` fill -- near-black in dark, white in light --
            // but the fill here is `bg-primary/20`, which leaves the surface
            // close to the sidebar's own background. So the token landed the
            // same colour as the backdrop: measured 1.35 (dark), 1.46 (light),
            // 1.34 (clean) against a 4.5 floor, making the page you are ON the
            // LEAST readable item in a nav whose inactive items are
            // `text-muted-foreground`. `text-primary` is not the fix either --
            // 3.98 light and 4.34 clean still fail. `text-foreground` measures
            // 10.29 / 11.68 / 13.38, and the active state keeps its four other
            // affordances: the fill, the border, the left bar and the primary
            // icon.
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.div
              layoutId="activeTab"
              className="absolute inset-0 bg-primary/20 border border-primary/30 rounded-lg"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
          )}
          {isActive && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1/2 bg-primary rounded-r-md" />
          )}
          <Icon className={`w-4 h-4 shrink-0 relative z-10 transition-colors ${isActive ? 'text-primary' : 'group-hover:text-foreground'}`} />
          {!collapsed && <span className="relative z-10 truncate">{displayLabel}</span>}
        </>
      )}
    </NavLink>
  );
}

/**
 * An agency header that opens and closes.
 *
 * Expanded state is per group and remembered for the session. Every group starts
 * CLOSED, so arriving shows the list of AGENCIES rather than every screen of
 * every agency at once -- which is the whole point of grouping them.
 *
 * On the collapsed rail the header shrinks to its icon and keeps working as the
 * toggle, and the screens beneath it show as icons only while it is open. The
 * sibling product lists every child flat on its rail; here that would be some
 * thirty icons across three agencies, so the grouping survives the collapse and
 * the same `expanded` state drives both widths.
 */
function NavGroup({
  group,
  collapsed,
  expanded,
  onToggle,
  onNavigate,
}: {
  group: NavGroupConfig;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('common');
  const displayLabel = group.labelKey ? t(group.labelKey, group.label ?? '') : group.label;
  const Icon = group.icon;

  if (collapsed) {
    return (
      <div className="py-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={displayLabel}
          title={displayLabel}
          className={`w-full flex items-center justify-center px-2 py-2 rounded-lg transition-colors ${
            expanded
              ? 'text-foreground bg-secondary/50'
              : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
          }`}
        >
          <Icon className="w-4 h-4 shrink-0" />
        </button>
        {expanded && (
          <div className="mt-0.5 space-y-0.5">
            {group.children.map((child) => (
              <NavItem key={child.to} {...child} collapsed onClick={onNavigate} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-left truncate">{displayLabel}</span>
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {group.children.map((child) => (
            <NavItem key={child.to} {...child} nested onClick={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

export function SidebarLayout({
  navigation = [],
  brandNameKey = 'branding.name',
  brandSubtitleKey = 'branding.subtitle',
  user,
  onLogout,
  breadcrumbKey = 'nav.dashboard',
  headerActions,
  breadcrumbs,
  children
}: SidebarLayoutProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  const { pathname } = useLocation();

  const [width, setWidth] = useState(() =>
    readStored(WIDTH_KEY, DEFAULT_WIDTH, (raw) => {
      const parsed = Number(raw);
      return Number.isFinite(parsed)
        ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, parsed))
        : DEFAULT_WIDTH;
    }),
  );
  const [collapsed, setCollapsed] = useState(() =>
    readStored(COLLAPSED_KEY, false, (raw) => raw === 'true'),
  );
  const [dragging, setDragging] = useState(false);

  const groups = useMemo(() => navigation.filter(isGroup), [navigation]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // The group holding the current screen, so the sidebar can say where you are.
  const activeGroupKey = useMemo(() => {
    let best: { key: string; score: number } | null = null;
    for (const group of groups) {
      const score = groupMatchScore(group, pathname);
      if (score > 0 && (!best || score > best.score)) best = { key: group.key, score };
    }
    return best?.key ?? null;
  }, [groups, pathname]);

  // Applied, not merely current: opening is a one-shot per active group, so a
  // user who deliberately collapses the group they are in does not have it
  // reopened under them on the next render.
  const openedFor = useRef<string | null>(null);

  // Seed every group CLOSED except the one holding the current screen.
  //
  // Entitlements decide which groups exist at all, so the set can change between
  // renders -- only unseen keys are seeded, or a re-render would slam shut a
  // group the user just opened.
  //
  // Seeding ALL of them closed was the original behaviour and it hid the one
  // thing the sidebar exists to show. The index redirects to /anatel/overview,
  // so signing in landed on a page whose nav entry was not rendered at all: the
  // active-item highlight lives inside this `expanded` branch, so "you are here"
  // was invisible until the reader thought to click a header. Same for a refresh
  // and for any shared link.
  useEffect(() => {
    const toOpen = activeGroupKey && openedFor.current !== activeGroupKey ? activeGroupKey : null;
    if (toOpen) openedFor.current = toOpen;
    setExpanded((prev) => {
      const next: Record<string, boolean> = { ...prev };
      let changed = false;
      for (const group of groups) {
        if (!(group.key in next)) {
          next[group.key] = false;
          changed = true;
        }
      }
      if (toOpen && !next[toOpen]) {
        next[toOpen] = true;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [groups, activeGroupKey]);

  const persistWidth = useCallback((next: number) => {
    const clamped = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next));
    setWidth(clamped);
    store(WIDTH_KEY, String(clamped));
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      store(COLLAPSED_KEY, String(!prev));
      return !prev;
    });
  }, []);

  // Pointer capture rather than window listeners: the drag keeps working when
  // the cursor leaves the handle, and it ends even if the pointer is released
  // over an iframe or outside the window.
  const onHandleDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (collapsed) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const onHandleMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    persistWidth(event.clientX);
  };
  const onHandleUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
  };

  const brandName = t(brandNameKey);
  // "Inteligência Regulatória" on a 60px rail is its initials, "IR".
  const brandMark = brandName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  const renderNav = (isCollapsed: boolean) => (
    <nav
      className="flex flex-col h-full bg-background/50 backdrop-blur-xl"
      aria-label={t('nav.primary', 'Primary navigation')}
    >
      {/* Brand */}
      <div className={`py-4 border-b border-border ${isCollapsed ? 'px-2 text-center' : 'px-4'}`}>
        {isCollapsed ? (
          <p className="font-bold text-foreground text-sm" title={brandName}>
            {brandMark}
          </p>
        ) : (
          <>
            <p className="font-bold text-foreground tracking-tight text-sm truncate">{brandName}</p>
            <p className="text-xs text-primary mt-0.5 font-medium truncate">{t(brandSubtitleKey)}</p>
          </>
        )}
      </div>

      {/* Links */}
      <div className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5 scrollbar-hide">
        {navigation.map((entry) =>
          isGroup(entry) ? (
            <NavGroup
              key={entry.key}
              group={entry}
              collapsed={isCollapsed}
              expanded={expanded[entry.key] ?? false}
              onToggle={() =>
                setExpanded((prev) => ({ ...prev, [entry.key]: !(prev[entry.key] ?? false) }))
              }
              onNavigate={() => setOpen(false)}
            />
          ) : (
            <NavItem
              key={entry.to}
              {...entry}
              collapsed={isCollapsed}
              onClick={() => setOpen(false)}
            />
          ),
        )}
      </div>

      {/* User strip */}
      <div className="px-3 py-4 border-t border-border bg-gradient-to-t from-foreground/5 to-transparent dark:from-foreground/10">
        {user && (
          <div className={`flex items-center gap-3 mb-3 ${isCollapsed ? 'justify-center' : 'pl-1'}`}>
            <div
              className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0 shadow-lg shadow-primary/20 border border-primary/10"
              title={isCollapsed ? user.name : undefined}
            >
              {user.initials}
            </div>
            {!isCollapsed && (
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-foreground truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{user.role}</p>
              </div>
            )}
          </div>
        )}
        <button
          onClick={onLogout}
          title={isCollapsed ? t('auth.logout', 'Log out') : undefined}
          aria-label={t('auth.logout', 'Log out')}
          className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground bg-secondary/20 hover:bg-destructive/10 hover:text-destructive transition-colors border border-transparent hover:border-destructive/20"
        >
          <LogOut className="w-3.5 h-3.5" />
          {!isCollapsed && t('auth.logout', 'Log out')}
        </button>
      </div>
    </nav>
  );

  return (
    <div className="h-full w-full bg-background flex overflow-hidden">
      {/* Desktop sidebar. Width is inline because it is user state, not a
          design token -- there is no Tailwind class for "whatever this person
          dragged it to". */}
      <aside
        data-testid="app-sidebar"
        style={{ width: collapsed ? RAIL_WIDTH : width }}
        className="hidden md:flex shrink-0 flex-col border-r border-border z-20 relative"
      >
        {renderNav(collapsed)}

        {/* Resize handle. A separator rather than a button: it has a value
            along an axis, which is what makes arrow keys the obvious
            keyboard affordance. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions --
            A FOCUSABLE separator carrying aria-valuenow/min/max and arrow-key
            handling is the WAI-ARIA "window splitter" widget, and this is that
            pattern to the letter. The rule classifies every separator as
            non-interactive because the unfocusable one (a visual rule) is;
            same disagreement as the tabIndex on <main> below, resolved the
            same way. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t('nav.resize', 'Resize sidebar')}
          aria-valuenow={collapsed ? RAIL_WIDTH : width}
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          tabIndex={collapsed ? -1 : 0}
          data-testid="sidebar-resize"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          onDoubleClick={() => persistWidth(DEFAULT_WIDTH)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') persistWidth(width - 16);
            if (event.key === 'ArrowRight') persistWidth(width + 16);
          }}
          className={`absolute inset-y-0 -right-1 w-2 z-30 ${
            collapsed ? 'hidden' : 'cursor-col-resize'
          } hover:bg-primary/30 focus-visible:bg-primary/40 focus-visible:outline-none ${
            dragging ? 'bg-primary/40' : ''
          }`}
        />
      </aside>

      {/* Mobile sidebar overlay. Always the full-width nav: a drawer that is
          already an overlay has nothing to gain from a rail. */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', bounce: 0, duration: 0.4 }}
              className="fixed inset-y-0 left-0 w-56 border-r border-border flex flex-col z-50 md:hidden bg-background"
            >
              {renderNav(false)}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        {/* Decorative Background Glows */}
        <div className="absolute top-[-10%] right-[-5%] w-[30%] h-[30%] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

        {/* Top bar */}
        <header className="h-12 flex items-center gap-3 px-4 border-b border-border shrink-0 bg-background/80 backdrop-blur-md sticky top-0 z-30">
          <button
            className="md:hidden text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setOpen(true)}
            aria-label={t('nav.openMenu', 'Open menu')}
          >
            <Menu className="w-5 h-5" />
          </button>
          {/* Desktop only: on mobile the sidebar is already an overlay, and a
              collapse control for a drawer is a control for nothing. */}
          <button
            className="hidden md:block text-muted-foreground hover:text-foreground transition-colors"
            onClick={toggleCollapsed}
            data-testid="sidebar-toggle"
            aria-label={
              collapsed
                ? t('nav.expandSidebar', 'Expand sidebar')
                : t('nav.collapseSidebar', 'Collapse sidebar')
            }
            aria-pressed={collapsed}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
          {breadcrumbs && breadcrumbs.length > 0 ? (
            <nav className="hidden md:flex items-center gap-1" aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, i) => {
                const label = crumb.labelKey ? t(crumb.labelKey, crumb.label ?? '') : (crumb.label ?? '');
                const isLast = i === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={i}>
                    {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />}
                    {crumb.to && !isLast ? (
                      <NavLink to={crumb.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                        {label}
                      </NavLink>
                    ) : (
                      <span className={`text-sm ${isLast ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          ) : (
            <>
              <ChevronRight className="w-4 h-4 text-muted-foreground hidden md:block" />
              <span className="text-sm font-medium text-muted-foreground hidden md:block">{t(breadcrumbKey, 'Dashboard')}</span>
            </>
          )}
          {headerActions ? (
            <div className="ml-auto flex items-center gap-2">{headerActions}</div>
          ) : null}
        </header>

        {/*
          Page content.

          tabIndex={0} is REQUIRED, not decorative. This is a scrollable region,
          and an analytical page can legitimately contain no focusable element at
          all -- the ANATEL overview is entirely headings, numbers and charts. A
          keyboard user then has no way to scroll it, which axe reports as
          `scrollable-region-focusable` (serious). Making the region itself
          focusable restores keyboard scrolling.

          Local divergence from lib-components, kept on purpose: the library is
          copy-FROM, and this fix belongs upstream too.
        */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex --
            The two checks genuinely disagree, and the renderer-based one wins.
            jsx-a11y forbids tabIndex on non-interactive elements as a static
            heuristic; axe MEASURES the rendered page and reports a serious
            violation without it, because a scrollable region with no focusable
            content cannot be scrolled by keyboard. WAI-ARIA's own scrollable-
            region guidance is to make the container focusable. */}
        <main tabIndex={0} className="flex-1 overflow-y-auto p-4 md:p-5 z-10 relative">
          {children}
        </main>
      </div>
    </div>
  );
}
